/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";

import { Hidden, Grid } from "@material-ui/core";
import {
  VideoLibrary,
  Assignment,
  CardGiftcard,
  CheckCircle,
} from "@material-ui/icons";

// TC Kit
import Parallax from "components/Parallax/Parallax.jsx";
import Button from "components/CustomButtons/Button.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import InfoArea from "components/InfoArea/InfoArea.jsx";

import { LoginDialog } from "client/views/Account/LoginDialog.tsx";
import { SignUpDialog } from "client/views/Account/SignUpDialog.tsx";
import {
  sLogin,
  sLoginFail,
  sBadSignIn,
  sSignup,
  sNoAccount,
  sGotAccount,
  sFreeAcnt,
  sFreeAcntMsg,
  sNtsVideo,
  sNtsVideoMsg,
  sClipbrd,
  sClipbrdMsg2,
  sTests,
  sTestsMsg,
} from "assets/jss/views/ResStrings";
import { idToLogin, idToSignup } from "assets/jss/views/ElementIDs";
import { backButton } from "components/Form/FormField";
import { Modal } from "components/Modal/Modal";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
const styles = createStyles(accountPageStyle as any);

interface Props extends WithStyles<typeof styles> {
  user: any;
  history: any;
  match: any;
}

const SignInPageBase: React.FC<Props> = ({
  history,
  user,
  classes,
  match,
}: Props) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  useEffect(() => {
    if (match.params.id === "err") {
      setAlertModal(
        <Modal
          title={sLoginFail}
          color={"warning"}
          onExit={() => history.replace("/signin")}>
          {sBadSignIn}
        </Modal>,
      );
      setShowAlert(true);
    }
  }, []);

  const clipbrdBenefit = [
    {
      title: sTests,
      description: sTestsMsg,
      icon: CheckCircle,
      iconColor: "test",
    },
    // {
    //   title: sLecVideo,
    //   description: sLecVideoMsg,
    //   icon: VideoLibrary,
    //   iconColor: "primary",
    // },
  ];

  const courseBenefit = [
    {
      title: sFreeAcnt,
      description: sFreeAcntMsg,
      icon: CardGiftcard,
      iconColor: "clipbrd",
    },
    {
      title: sClipbrd,
      description: sClipbrdMsg2,
      icon: Assignment,
      iconColor: "success",
    },
    {
      title: sNtsVideo,
      description: sNtsVideoMsg,
      icon: VideoLibrary,
      iconColor: "note",
    },
  ];

  const renderButtons = () => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Button
        id={idToSignup}
        style={{ margin: "2rem 2rem 0" }}
        color="primary"
        onClick={() => setShowSignUp(true)}>
        <span className={classes.showFor1024Up}>
          {sNoAccount}&nbsp;&nbsp;<b>{sSignup}</b>
        </span>
        <span className={classes.showFor1023Down}>
          {sNoAccount}
          <br />
          <b>{sSignup}</b>
        </span>
      </Button>
      <Button
        id={idToLogin}
        style={{ margin: "1rem 2rem 0" }}
        color="success"
        onClick={() => setShowLogin(true)}>
        <span className={classes.showFor1024Up}>
          {sGotAccount}&nbsp;&nbsp;<b>{sLogin}</b>
        </span>
        <span className={classes.showFor1023Down}>
          {sGotAccount}
          <br />
          <b>{sLogin}</b>
        </span>
      </Button>
    </div>
  );

  const renderClipbrdBenefit = () => (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {clipbrdBenefit.map((item, idx) => (
        <InfoArea
          key={idx}
          className={classes.infoArea}
          title={item.title}
          description={item.description}
          icon={item.icon}
          iconColor={item.iconColor}
        />
      ))}
    </div>
  );

  // This page should not show when user already logged in
  if (user) {
    history.push("/");
  }

  const mainImg = require("assets/img/desk3.jpg");
  return (
    <div className={classes.signinContainer}>
      {showAlert && alertModal}
      <Parallax image={mainImg} />
      {showLogin && (
        <LoginDialog
          onExit={() => setShowLogin(false)}
          onSignUp={() => setShowSignUp(true)}
        />
      )}
      {showSignUp && (
        <SignUpDialog
          onExit={() => setShowSignUp(false)}
          onLogin={() => setShowLogin(true)}
        />
      )}
      <div className={classes.signinPanel}>
        {backButton(history)}
        <Grid container justify="center">
          <Grid item sm={12} md={10}>
            <Card className={classes.cardSignup}>
              <Hidden smDown implementation="css">
                <h2 className={classes.cardTitle}>
                  {"Study, with Efficiency and Comfort"}
                </h2>
              </Hidden>
              <CardBody>
                <Grid container justify="center">
                  <Grid item sm={12} md={6} lg={5}>
                    <Hidden mdUp implementation="css">
                      {renderButtons()}
                    </Hidden>
                    {courseBenefit.map((item, idx) => (
                      <InfoArea
                        key={idx}
                        className={classes.infoArea}
                        title={item.title}
                        description={item.description}
                        icon={item.icon}
                        iconColor={item.iconColor}
                      />
                    ))}
                  </Grid>
                  <Grid
                    item
                    style={{ display: "flex", flexDirection: "column" }}
                    sm={12}
                    md={6}
                    lg={5}>
                    {renderClipbrdBenefit()}
                    <Hidden smDown implementation="css">
                      {renderButtons()}
                    </Hidden>
                  </Grid>
                </Grid>
              </CardBody>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
  };
}

const base: any = SignInPageBase;
export const SignInPage = {
  component: withStyles(styles)(connect(mapStateToProps)(base)),
};

export const SignInPageTest = withStyles(styles)(base);
